/* eslint-disable sonarjs/cognitive-complexity, complexity */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { SPLAT_CONFIG } from '../../../brands/default/config';
import { SHOW_PRICE_TYPES } from '../../../brands/default/constants';
import { NORMAL_PRICE_SPLAT_COMPONENT } from '../../../brands/default/content/components/deal';
import {
  showPriceType,
  showPrice,
  getPrice,
  getText,
  getIsChooseYourProductDeal,
  getMinOriginalPrice,
  showPerNight,
} from '../../../helpers/deals';
import { isMobileViewport, useScreenBreakpoint } from '../../../helpers/screen';
import useClientSideEffect from '../../../helpers/useClientSideEffect';
import ThemeContext from '../../../providers/ThemeProvider';

const NormalPriceSplat = ({
  className,
  coinFlipAnimation,
  columnSelection,
  deal = {},
  discount = false,
  displayDiscountAndOriginalPrice = false,
  displayEstimatedText = false,
  estimatedText = null,
  forcePrice = true,
  isHideIfNoDiscount = true,
  isPPVisible = false,
  isNationalDeal = false,
  isMainDeal = false,
  isHideSplat = false,
  isProductPage = false,
  rowSelection,
  secondCheckout = false,
  showVipSplat = false,
  visible = true,
}) => {
  const theme = useContext(ThemeContext);
  const priceContainer = useRef();
  const [shrinkPrice, setShrinkPrice] = useState(false);
  const [transform, setTransform] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const breakpoint = useScreenBreakpoint();

  const isChooseYourProductDeal = getIsChooseYourProductDeal(deal);
  const priceType = showPriceType(
    deal,
    discount,
    forcePrice,
    isHideIfNoDiscount,
  );
  const originalPrice = getMinOriginalPrice(deal);
  const defaultPrice = getPrice({
    columnSelection,
    deal,
    displayDiscountAndOriginalPrice,
    forceDecimals: true,
    isNationalDeal,
    isPPVisible,
    priceType,
    rowSelection,
  });
  const showUpTo = () => {
    if (
      priceType === SHOW_PRICE_TYPES.showDiscountIndicative &&
      !isNationalDeal
    )
      return true;
    else if (isNationalDeal && !isMainDeal && !isPPVisible) {
      if (
        (deal.priceIndicative && deal.discountPercentage) ||
        deal.display?.discountAmount
      ) {
        return true;
      }

      return false;
    }

    return false;
  };
  const nonProductPagePrice = defaultPrice;
  const [price, setPrice] = useState(defaultPrice);

  useEffect(() => {
    // Check if it's a product page and columnSelection is greater than 0 or if its a isChooseYourProductDeal
    if (isProductPage && (columnSelection > 0 || isChooseYourProductDeal)) {
      setPrice(
        getPrice({
          columnSelection,
          deal,
          displayDiscountAndOriginalPrice,
          forceDecimals: true,
          isNationalDeal,
          isPPVisible,
          priceType,
          rowSelection,
        }),
      );
    }
  }, [isProductPage, columnSelection, rowSelection]);

  useEffect(() => {
    setIsMobile(isMobileViewport());
  }, [breakpoint]);

  useClientSideEffect(() => {
    setShrinkPrice(
      priceContainer?.current?.offsetWidth > SPLAT_CONFIG.SPLAT_MAX_WIDTH_PX,
    );
  }, [priceContainer]);

  useEffect(() => {
    if (showVipSplat && coinFlipAnimation) {
      const timeoutId = setTimeout(() => {
        setTransform(true);
      }, 3_000);

      return () => clearTimeout(timeoutId);
    }

    return null;
  }, [showVipSplat, coinFlipAnimation]);

  // hide splat
  if (priceType === SHOW_PRICE_TYPES.hidden || isHideSplat) return null;

  return (
    <div
      className={classNames('splat', className, {
        'splat--discount': discount,
        'splat--discount-indicative':
          priceType === SHOW_PRICE_TYPES.showDiscountIndicative,
        'splat--price': priceType !== SHOW_PRICE_TYPES.showDiscountIndicative,
        'splat--visible': visible,
      })}
    >
      <div className="splat__text">
        <p className="splat__now">
          {getText({
            deal,
            displayDiscountAndOriginalPrice,
            isNationalDeal,
            isPPVisible,
            priceType,
            rowSelection,
            secondCheckout,
          })}
        </p>
        {showUpTo() && (
          <p className="splat__upto">{NORMAL_PRICE_SPLAT_COMPONENT.upTo}</p>
        )}
        <p
          className={classNames('splat__price', {
            'splat__price--small': shrinkPrice,
          })}
          dangerouslySetInnerHTML={{
            __html: isProductPage ? price : nonProductPagePrice,
          }}
          ref={priceContainer}
        />
        {showPerNight({ deal, priceType }) && (
          <p className="splat__per-night">Per Night</p>
        )}
        {!isMobile && displayEstimatedText && (
          <p className="splat__adminfee">{estimatedText || null} </p>
        )}
        {(priceType === SHOW_PRICE_TYPES.showOriginalPrice ||
          displayDiscountAndOriginalPrice) &&
          isPPVisible &&
          isNationalDeal &&
          !isMainDeal && (
            <p
              className="splat__original-price"
              dangerouslySetInnerHTML={{
                __html: showPrice({
                  currency: deal?.currency,
                  dealPrice: originalPrice || deal.originalPrice,
                  forceDecimals: true,
                  pricePerPerson: false,
                }),
              }}
            />
          )}
        {isMainDeal &&
          (priceType === SHOW_PRICE_TYPES.showOriginalPrice ||
            displayDiscountAndOriginalPrice) && (
            <p
              className="splat__original-price"
              dangerouslySetInnerHTML={{
                __html: showPrice({
                  currency: deal?.currency,
                  dealPrice: originalPrice || deal.originalPrice,
                  forceDecimals: true,
                  pricePerPerson: false,
                }),
              }}
            />
          )}
        {isMobile && displayEstimatedText && (
          <p className="splat__adminfee-mobile">{estimatedText || null} </p>
        )}
      </div>
      <style jsx>{`
        .splat {
          width: 85px;
          height: 85px;
          overflow: hidden;
          position: absolute;
          bottom: 0;
          right: 0;
          background: url(${theme.images.dealsplat}) no-repeat;
          background-position: 24px 24px;
          background-size: 100px;
          z-index: 100;
          cursor: pointer;
          visibility: hidden;
          pointer-events: none;
          transform: ${transform ? 'rotateY(90deg)' : 'rotateY(0deg)'};
          transition: ${transform ? 'all 0.4s ease-in' : ''};
        }

        .splat--visible {
          visibility: visible;
        }

        .splat__text {
          z-index: 999;
          position: absolute;
          bottom: ${secondCheckout ? '35px' : '4px'};
          right: 0;
          color: ${theme.colors.splattext};
          margin: 0;
          text-align: center;
          line-height: 1em;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 54px;
          min-height: 38px;
          font-size: 17px;
        }

        .splat__now {
          font-size: 0.7em;
          text-transform: uppercase;
          font-weight: bold;
          line-height: 1em;
          margin: 0;
        }

        .splat__upto {
          font-size: 0.4em;
          text-transform: uppercase;
          line-height: 1em;
          margin: 0;
        }

        .splat__price {
          font-weight: bold;
          line-height: 1.2em;
          margin: 0;
        }

        .splat__per-night {
          font-size: 9px;
          line-height: 8px;
          margin: -3px 0 1px 0;
        }

        :global(.cols-50 .splat__price--small) {
          font-size: 16px;
        }

        .xs .splat__text .splat__price--small {
          font-size: 14px;
        }

        .splat__original-price {
          position: relative;
          font-size: 0.6em;
          line-height: 1em;
          margin: 0;
          margin-top: ${displayEstimatedText ? '5px' : '0'};
        }
        .splat__adminfee {
          color: ${theme.colors.adminfeetext};
          font-size: 0.5em;
          line-height: 1em;
          margin: 0;
          margin-top: ${priceType === SHOW_PRICE_TYPES.showPrice
            ? '15px'
            : '0'};
        }
        .splat__adminfee-mobile {
          color: ${theme.colors.primary};
          font-size: 0.4em;
          line-height: 8px;
          margin: 0;
          margin-top: ${priceType === SHOW_PRICE_TYPES.showOriginalPrice
            ? '18px'
            : '15px'};
          margin-right: 10px;
        }

        .splat__original-price:before {
          position: absolute;
          content: '';
          left: 0;
          top: 50%;
          right: 0;
          width: 120%;
          margin-left: -10%;
          border-top: 1px solid;
          -webkit-transform: rotate(-11deg);
          -ms-transform: rotate(-11deg);
          transform: rotate(-11deg);
        }

        .splat :global(.price__cents) {
          font-size: 0.6em;
          line-height: 0;
        }

        .splat :global(.price__pp) {
          font-size: 0.5em;
          font-weight: normal;
          vertical-align: super;
          line-height: 0;
        }
        .bottom-right {
          bottom: initial;
          background-position: 10px -40px;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .splat {
            background-position: 10px 12px;
            background-size: 100px;
            background-size: 100px;
            width: 75px;
            height: 75px;
          }

          .splat__text {
            bottom: ${secondCheckout ? '25px' : '4px'};
            font-size: 19px;
            min-height: 30px;
          }

          .top-right {
            top: 0;
            bottom: initial;
            background-position: 10px -45px;
          }
          .bottom-right {
            bottom: 0;
            bottom: initial;
            background-position: 10px -35px;
          }
          .top-right .splat__text {
            top: 2px;
            bottom: auto;
            min-height: 51px;
          }

          .xs {
            background-position: 22px 24px;
            background-size: 80px;
          }
          .xs .splat__text {
            font-size: 16px;
            min-width: 48px;
            min-height: 38px;
          }
          .top-right.xs {
            background-position: 22px -30px;
          }
          .top-right.xs .splat__text {
            min-height: 48px;
          }

          .xl {
            width: 100px;
            height: 100px;
            background-position: 10px 12px;
            background-size: 140px;
          }
          .xl .splat__text {
            font-size: 24px;
            min-height: 70px;
            min-width: 80px;
          }
        }

        .main-deal--mobile {
          top: 0;
          bottom: initial;
          background-size: 110px;
          background-position: -6px -62px;
        }
        .main-deal--mobile .splat__text {
          top: 9px;
          bottom: auto;
          min-height: 60px;
          min-width: 60px;
          font-size: 20px;
          margin-right: ${displayEstimatedText ? '-10px' : '7px'};
        }
        .main-deal--mobile--product-page {
          bottom: initial;
          background-size: 110px;
          background-position: 0px -30px;
        }
        .main-deal--mobile--product-page .splat__text {
          top: 2px;
          bottom: auto;
          min-height: 60px;
          min-width: 60px;
          font-size: 20px;
        }
        .full {
          width: 130px;
          height: 135px;
          background-size: contain;
          background-position: 0 0;
        }
        .full .splat__text {
          width: 100%;
          height: 100%;
          left: 0;
          top: 0;
          bottom: 0;
          right: 0;
        }
        .full .splat__price {
          font-size: 32px;
        }
        .full .splat__now {
          font-size: 0.8em;
        }
        .full .splat__upto {
          font-size: 0.7em;
        }
        .full .splat__original-price {
          font-size: 1em;
        }
      `}</style>
    </div>
  );
};

NormalPriceSplat.propTypes = {
  className: PropTypes.string,
  coinFlipAnimation: PropTypes.bool,
  columnSelection: PropTypes.number,
  deal: PropTypes.object,
  discount: PropTypes.bool,
  displayDiscountAndOriginalPrice: PropTypes.bool,
  displayEstimatedText: PropTypes.bool,
  estimatedText: PropTypes.string,
  forcePrice: PropTypes.bool,
  isHideIfNoDiscount: PropTypes.bool,
  isHideSplat: PropTypes.bool,
  isMainDeal: PropTypes.bool,
  isNationalDeal: PropTypes.bool,
  isPPVisible: PropTypes.bool,
  isProductPage: PropTypes.bool,
  rowSelection: PropTypes.number,
  secondCheckout: PropTypes.bool,
  showVipSplat: PropTypes.bool,
  visible: PropTypes.bool,
};

export default NormalPriceSplat;
